import { useEffect } from "react";
import { Hero } from "@/components/marketing/Hero";
import { MobileAppSection } from "@/components/marketing/MobileAppSection";
import { FeatureShowcase } from "@/components/marketing/FeatureShowcase";
import { CommunitySection } from "@/components/marketing/CommunitySection";
import { PricingSection } from "@/components/marketing/PricingSection";
import { Testimonials } from "@/components/marketing/Testimonials";
import { CTABanner } from "@/components/marketing/CTABanner";
import { StickyMobileCta } from "@/components/marketing/StickyMobileCta";
import { readPromoFromUrl, persistPromo } from "@/lib/promo";
import { trackPromoCodeSeen } from "@/lib/analytics";

const LANDING_DESCRIPTION =
  "Track every service, mod, trip, meet, and ride for your cars, bikes, and powersports. Free tier covers your first three vehicles plus Spotted, Meets RSVPs, and 5 AI document scans.";

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://revhead.pro/#org",
      name: "RevHead Pro",
      url: "https://revhead.pro",
      logo: "https://revhead.pro/logo-512.png",
    },
    {
      "@type": "SoftwareApplication",
      name: "RevHead Pro",
      applicationCategory: "LifestyleApplication",
      operatingSystem: "iOS, Android, Web",
      url: "https://revhead.pro",
      description: LANDING_DESCRIPTION,
      offers: [
        { "@type": "Offer", price: "0", priceCurrency: "USD", name: "Free" },
        { "@type": "Offer", price: "6.99", priceCurrency: "USD", name: "Pro Monthly" },
        { "@type": "Offer", price: "59.99", priceCurrency: "USD", name: "Pro Annual" },
      ],
    },
  ],
};

export function meta() {
  return [
    { title: "RevHead Pro — Track Your Rides" },
    { name: "description", content: LANDING_DESCRIPTION },
    { property: "og:title", content: "RevHead Pro — Track Your Rides" },
    { property: "og:description", content: LANDING_DESCRIPTION },
    { property: "og:url", content: "https://revhead.pro/" },
    { tagName: "link", rel: "canonical", href: "https://revhead.pro/" },
    { "script:ld+json": JSON_LD },
  ];
}

export default function Landing() {
  // Promo machinery stays alive for partner campaigns: any `?promo=<code>`
  // landing still persists the code to localStorage and fires analytics, so
  // /upgrade can pick it up downstream. We just no longer surface a public
  // banner advertising it — the in-app "Have a code?" link is the entry
  // point for users who actually have one.
  useEffect(() => {
    const captured = readPromoFromUrl();
    if (!captured) return;
    persistPromo(captured);

    const sessionFlag = `rh_promo_seen_${captured.code}`;
    if (!sessionStorage.getItem(sessionFlag)) {
      trackPromoCodeSeen({
        code: captured.code,
        source: captured.source,
        medium: captured.medium,
        campaign: captured.campaign,
      });
      sessionStorage.setItem(sessionFlag, "1");
    }
  }, []);

  return (
    <>
      <Hero />
      <MobileAppSection />
      <FeatureShowcase />
      <CommunitySection />
      <PricingSection />
      <Testimonials />
      <CTABanner />
      {/* Leave room at the page bottom for the sticky CTA bar on mobile. */}
      <div className="h-20 md:hidden" aria-hidden />
      <StickyMobileCta />
    </>
  );
}
